import React, { useState, useEffect } from 'react'
import { Brain, Target, Users, Star, TrendingUp, Filter, Search, Sparkles, Award, Clock, DollarSign, Heart, Shield } from 'lucide-react'
import { GuruProfile, GuruConnectApi } from '../../services/guruConnectApi'

interface UserHealthProfile {
  conditions: string[]
  goals: string[]
  preferences: {
    session_type: 'video' | 'in_person' | 'hybrid'
    max_price: number
    availability: string[]
    communication_style: 'formal' | 'casual' | 'motivational'
    experience_level: 'beginner' | 'intermediate' | 'advanced'
  }
  health_data: {
    glucose_levels?: number[]
    weight_history?: number[]
    medication_compliance?: number
    activity_level: 'low' | 'moderate' | 'high'
    stress_level: 'low' | 'moderate' | 'high'
  }
}

interface MatchScore {
  guru: GuruProfile
  score: number
  factors: {
    specialty_match: number
    experience_match: number
    price_match: number
    availability_match: number
    communication_match: number
    success_rate: number
  }
  reasoning: string[]
}

interface AIGuruMatchingProps {
  userHealthProfile: UserHealthProfile
  onGuruSelect: (guru: GuruProfile) => void
}

export const AIGuruMatching: React.FC<AIGuruMatchingProps> = ({
  userHealthProfile,
  onGuruSelect
}) => {
  const [matches, setMatches] = useState<MatchScore[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({
    minScore: 0,
    maxPrice: 1000,
    specialties: [] as string[],
    experience: [] as string[]
  })
  const [showDetails, setShowDetails] = useState<string | null>(null)

  useEffect(() => {
    generateMatches()
  }, [userHealthProfile])

  const generateMatches = async () => {
    try {
      setLoading(true)
      setError('')
      
      // In a real app, this would call an AI service to analyze user data and find matches
      const allGurus = await GuruConnectApi.searchGurus({ limit: 100, is_verified: true })
      
      const scoredMatches: MatchScore[] = allGurus.gurus.map(guru => {
        const score = calculateMatchScore(guru, userHealthProfile)
        return {
          guru,
          score: score.total,
          factors: score.factors,
          reasoning: score.reasoning
        }
      })

      // Sort by match score (highest first)
      scoredMatches.sort((a, b) => b.score - a.score)
      setMatches(scoredMatches)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate matches')
    } finally {
      setLoading(false)
    }
  }

  const calculateMatchScore = (guru: GuruProfile, profile: UserHealthProfile) => {
    let specialtyMatch = 0
    let experienceMatch = 0
    let priceMatch = 0
    let availabilityMatch = 0
    let communicationMatch = 0
    let successRate = 0
    const reasoning: string[] = []

    // Specialty matching (40% weight)
    const userConditions = profile.conditions
    const userGoals = profile.goals
    const guruSpecialties = guru.specialties
    
    const conditionMatches = userConditions.filter(condition => 
      guruSpecialties.some(specialty => 
        specialty.toLowerCase().includes(condition.toLowerCase()) ||
        condition.toLowerCase().includes(specialty.toLowerCase())
      )
    ).length

    const goalMatches = userGoals.filter(goal => 
      guruSpecialties.some(specialty => 
        specialty.toLowerCase().includes(goal.toLowerCase()) ||
        goal.toLowerCase().includes(specialty.toLowerCase())
      )
    ).length

    specialtyMatch = ((conditionMatches / userConditions.length) * 0.6 + 
                     (goalMatches / userGoals.length) * 0.4) * 100

    if (specialtyMatch > 0) {
      reasoning.push(`Specialty match: ${Math.round(specialtyMatch)}% - ${conditionMatches} conditions and ${goalMatches} goals aligned`)
    }

    // Experience level matching (20% weight)
    const userExperience = profile.preferences.experience_level
    const guruExperience = guru.years_experience
    
    if (userExperience === 'beginner' && guruExperience >= 1 && guruExperience <= 5) {
      experienceMatch = 100
      reasoning.push('Perfect experience level for beginners')
    } else if (userExperience === 'intermediate' && guruExperience >= 3 && guruExperience <= 10) {
      experienceMatch = 100
      reasoning.push('Ideal experience level for intermediate users')
    } else if (userExperience === 'advanced' && guruExperience >= 8) {
      experienceMatch = 100
      reasoning.push('High experience level suitable for advanced users')
    } else {
      experienceMatch = Math.max(0, 100 - Math.abs(guruExperience - 5) * 10)
      reasoning.push(`Experience level: ${guruExperience} years (${userExperience} preferred)`)
    }

    // Price matching (15% weight)
    const userMaxPrice = profile.preferences.max_price
    const guruPrice = guru.session_rate
    
    if (guruPrice <= userMaxPrice) {
      priceMatch = 100
      reasoning.push(`Price within budget: $${guruPrice}/hr`)
    } else {
      priceMatch = Math.max(0, 100 - ((guruPrice - userMaxPrice) / userMaxPrice) * 100)
      reasoning.push(`Price: $${guruPrice}/hr (budget: $${userMaxPrice})`)
    }

    // Availability matching (10% weight)
    // In a real app, this would check actual availability
    availabilityMatch = 85 // Mock score
    reasoning.push('Good availability for your preferred times')

    // Communication style matching (10% weight)
    const userStyle = profile.preferences.communication_style
    // Mock communication style matching
    communicationMatch = 90
    reasoning.push(`Communication style: ${userStyle} (well-matched)`)

    // Success rate (5% weight)
    successRate = guru.average_rating * 20 // Convert 5-star rating to percentage
    reasoning.push(`Success rate: ${guru.average_rating.toFixed(1)}/5 stars`)

    const total = (specialtyMatch * 0.4) + 
                  (experienceMatch * 0.2) + 
                  (priceMatch * 0.15) + 
                  (availabilityMatch * 0.1) + 
                  (communicationMatch * 0.1) + 
                  (successRate * 0.05)

    return {
      total: Math.round(total),
      factors: {
        specialty_match: Math.round(specialtyMatch),
        experience_match: Math.round(experienceMatch),
        price_match: Math.round(priceMatch),
        availability_match: Math.round(availabilityMatch),
        communication_match: Math.round(communicationMatch),
        success_rate: Math.round(successRate)
      },
      reasoning
    }
  }

  const filteredMatches = matches.filter(match => {
    return match.score >= selectedFilters.minScore &&
           match.guru.session_rate <= selectedFilters.maxPrice &&
           (selectedFilters.specialties.length === 0 || 
            selectedFilters.specialties.some(s => match.guru.specialties.includes(s))) &&
           (selectedFilters.experience.length === 0 ||
            selectedFilters.experience.includes(match.guru.years_experience >= 8 ? 'expert' : 
                                               match.guru.years_experience >= 5 ? 'experienced' : 'beginner'))
  })

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200'
    if (score >= 80) return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-200'
    if (score >= 70) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200'
    return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-200'
  }

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Perfect Match'
    if (score >= 80) return 'Excellent Match'
    if (score >= 70) return 'Good Match'
    if (score >= 60) return 'Fair Match'
    return 'Poor Match'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">AI is analyzing your profile...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">AI-Powered Guru Matching</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Our AI analyzes your health profile to find the perfect health professional match
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <Target className="w-5 h-5 text-purple-500" />
              <span className="font-semibold text-gray-900 dark:text-white">Your Goals</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {userHealthProfile.goals.map((goal, index) => (
                <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full text-xs">
                  {goal}
                </span>
              ))}
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <Heart className="w-5 h-5 text-red-500" />
              <span className="font-semibold text-gray-900 dark:text-white">Health Focus</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {userHealthProfile.conditions.map((condition, index) => (
                <span key={index} className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full text-xs">
                  {condition}
                </span>
              ))}
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="font-semibold text-gray-900 dark:text-white">Budget</span>
            </div>
            <p className="text-lg font-bold text-green-600">
              ${userHealthProfile.preferences.max_price}/hr
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Refine Matches</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Minimum Match Score
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={selectedFilters.minScore}
              onChange={(e) => setSelectedFilters(prev => ({ ...prev, minScore: parseInt(e.target.value) }))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span>{selectedFilters.minScore}%</span>
              <span>100%</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Max Price per Hour
            </label>
            <input
              type="range"
              min="50"
              max="500"
              step="25"
              value={selectedFilters.maxPrice}
              onChange={(e) => setSelectedFilters(prev => ({ ...prev, maxPrice: parseInt(e.target.value) }))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>$50</span>
              <span>${selectedFilters.maxPrice}</span>
              <span>$500</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Specialties
            </label>
            <select
              multiple
              value={selectedFilters.specialties}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value)
                setSelectedFilters(prev => ({ ...prev, specialties: values }))
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="diabetes">Diabetes</option>
              <option value="weight_loss">Weight Loss</option>
              <option value="nutrition">Nutrition</option>
              <option value="fitness">Fitness</option>
              <option value="mental_health">Mental Health</option>
              <option value="cardiology">Cardiology</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Experience Level
            </label>
            <select
              multiple
              value={selectedFilters.experience}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value)
                setSelectedFilters(prev => ({ ...prev, experience: values }))
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="beginner">Beginner (1-3 years)</option>
              <option value="experienced">Experienced (4-7 years)</option>
              <option value="expert">Expert (8+ years)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Matches */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            AI Recommendations ({filteredMatches.length})
          </h3>
          <button
            onClick={generateMatches}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            <span>Refresh Matches</span>
          </button>
        </div>

        {filteredMatches.length === 0 ? (
          <div className="text-center py-12">
            <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Matches Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your filters or health profile to find better matches.
            </p>
          </div>
        ) : (
          filteredMatches.map((match, index) => (
            <div
              key={match.guru.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start space-x-4">
                {/* Match Score */}
                <div className="flex-shrink-0">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-lg ${getScoreColor(match.score)}`}>
                    {match.score}%
                  </div>
                  <p className="text-xs text-center mt-1 text-gray-600 dark:text-gray-400">
                    {getScoreLabel(match.score)}
                  </p>
                </div>

                {/* Guru Info */}
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <img
                      src={GuruConnectApi.getGuruAvatar(match.guru)}
                      alt={GuruConnectApi.getGuruDisplayName(match.guru)}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {GuruConnectApi.getGuruDisplayName(match.guru)}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {GuruConnectApi.getGuruRoleDisplay(match.guru.guru_role)}
                      </p>
                    </div>
                    {match.guru.is_verified && (
                      <Shield className="w-5 h-5 text-blue-500" />
                    )}
                  </div>

                  {/* Match Factors */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Specialty</div>
                      <div className="text-sm font-medium">{match.factors.specialty_match}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Experience</div>
                      <div className="text-sm font-medium">{match.factors.experience_match}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Price</div>
                      <div className="text-sm font-medium">{match.factors.price_match}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Availability</div>
                      <div className="text-sm font-medium">{match.factors.availability_match}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Communication</div>
                      <div className="text-sm font-medium">{match.factors.communication_match}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Success Rate</div>
                      <div className="text-sm font-medium">{match.factors.success_rate}%</div>
                    </div>
                  </div>

                  {/* Specialties */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {match.guru.specialties.slice(0, 3).map((specialty, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-xs">
                        {specialty}
                      </span>
                    ))}
                    {match.guru.specialties.length > 3 && (
                      <span className="text-xs text-gray-500">+{match.guru.specialties.length - 3} more</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span>{match.guru.average_rating.toFixed(1)}</span>
                        <span>({match.guru.total_reviews})</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span>{GuruConnectApi.formatTrustScore(match.guru.trust_score)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span>{GuruConnectApi.formatPrice(match.guru.session_rate)}/hr</span>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowDetails(showDetails === match.guru.id ? null : match.guru.id)}
                        className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        {showDetails === match.guru.id ? 'Hide' : 'Details'}
                      </button>
                      <button
                        onClick={() => onGuruSelect(match.guru)}
                        className="px-4 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md font-medium transition-colors"
                      >
                        Select Guru
                      </button>
                    </div>
                  </div>

                  {/* Detailed Reasoning */}
                  {showDetails === match.guru.id && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <h5 className="font-medium text-gray-900 dark:text-white mb-2">Why This Match?</h5>
                      <ul className="space-y-1">
                        {match.reasoning.map((reason, index) => (
                          <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start space-x-2">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
} 